import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

const pulseGlow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 8px rgba(11, 145, 214, 0.4)); }
  50% { filter: drop-shadow(0 0 16px rgba(11, 145, 214, 0.6)); }
`;

const GaugeWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
}));

const Title = styled(Typography)(({ theme }) => ({
  color: '#1f2937',
  fontSize: '18px',
  fontWeight: 600,
  textAlign: 'center',
  fontFamily: 'Inter, sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px'
  }
}));

const SVGContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '280px',
  height: '160px',
  [theme.breakpoints.down('md')]: {
    width: '240px',
    height: '140px'
  },
  [theme.breakpoints.down('sm')]: {
    width: '200px',
    height: '120px'
  }
}));

const ScoreDisplay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '8px',
  left: '50%',
  transform: 'translateX(-50%)',
  textAlign: 'center',
}));

const ScoreValue = styled(Typography)(({ theme }) => ({
  fontSize: '32px',
  fontWeight: 700,
  color: '#1f2937',
  fontFamily: 'Inter, sans-serif',
  lineHeight: 1,
  [theme.breakpoints.down('md')]: {
    fontSize: '28px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px'
  }
}));

const ScoreMax = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: '#9ca3af',
  fontFamily: 'Inter, sans-serif',
  marginTop: '2px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px'
  }
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#6b7280',
  fontWeight: 500,
  textAlign: 'center',
  fontFamily: 'Inter, sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px'
  }
}));

const StatusBadge = styled(Box)(({ theme, status }) => {
  const colors = {
    poor: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
    fair: { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },
    good: { bg: '#fefce8', text: '#ca8a04', border: '#fef08a' },
    great: { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
    excellent: { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' }
  };
  const c = colors[status] || colors.fair;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    borderRadius: '20px',
    backgroundColor: c.bg,
    border: `1px solid ${c.border}`,
    marginTop: '8px',
    '& span': {
      fontSize: '13px',
      fontWeight: 600,
      color: c.text,
      fontFamily: 'Inter, sans-serif',
      textTransform: 'capitalize'
    }
  };
});

function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

function getScoreStatus(score, maxScore) {
  const pct = maxScore ? score / maxScore : 0;
  if (pct < 0.2) return 'poor';
  if (pct < 0.4) return 'fair';
  if (pct < 0.6) return 'good';
  if (pct < 0.8) return 'great';
  return 'excellent';
}

function getScoreColor(pct) {
  if (pct < 0.2) return '#ef4444';
  if (pct < 0.4) return '#f97316';
  if (pct < 0.6) return '#eab308';
  if (pct < 0.8) return '#22c55e';
  return '#10b981';
}

const SemiCircularGauge = ({ score = 0, maxScore = 300, title = 'Performance Gauge', showLabels = true }) => {
  const [displayScore, setDisplayScore] = useState(0);
  const [animatedPct, setAnimatedPct] = useState(0);

  const targetPct = useMemo(() => clamp(maxScore ? score / maxScore : 0, 0, 1), [score, maxScore]);
  const status = useMemo(() => getScoreStatus(score, maxScore), [score, maxScore]);

  useEffect(() => {
    const duration = 1200;
    const start = performance.now();
    const fromScore = displayScore;
    const toScore = clamp(score, 0, maxScore);
    const fromPct = animatedPct;
    const toPct = targetPct;

    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayScore(Math.round(fromScore + (toScore - fromScore) * eased));
      setAnimatedPct(fromPct + (toPct - fromPct) * eased);
      if (t < 1) {
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [score, maxScore, targetPct]);

  const cx = 140;
  const cy = 140;
  const radius = 110;
  const strokeWidth = 20;
  const startAngle = 180;
  const endAngle = 0;
  const arcLength = Math.PI * radius;

  const polarToCartesian = (angle) => {
    const rad = (angle * Math.PI) / 180;
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad)
    };
  };

  const describeArc = (startAng, endAng) => {
    const s = polarToCartesian(startAng);
    const e = polarToCartesian(endAng);
    const largeArc = endAng - startAng <= 180 ? 0 : 1;
    return `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${largeArc} 1 ${e.x} ${e.y}`;
  };

  const progressAngle = startAngle + (endAngle - startAngle + 360) % 360 * animatedPct;
  const progressOffset = arcLength * (1 - animatedPct);

  const needleAngle = startAngle + 180 * animatedPct;
  const needleRad = (needleAngle * Math.PI) / 180;
  const needleLength = radius - 25;
  const needleTip = {
    x: cx + needleLength * Math.cos(needleRad),
    y: cy + needleLength * Math.sin(needleRad)
  };

  const tickMarks = [];
  for (let i = 0; i <= 10; i++) {
    const angle = 180 + (i * 180) / 10;
    const rad = (angle * Math.PI) / 180;
    const innerR = radius + strokeWidth / 2 + 4;
    const outerR = radius + strokeWidth / 2 + (i % 5 === 0 ? 12 : 8);
    tickMarks.push({
      x1: cx + innerR * Math.cos(rad),
      y1: cy + innerR * Math.sin(rad),
      x2: cx + outerR * Math.cos(rad),
      y2: cy + outerR * Math.sin(rad),
      major: i % 5 === 0
    });
  }

  const gradientId = `gauge-gradient-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <GaugeWrapper>
      {title && <Title>{title}</Title>}
      <SVGContainer>
        <svg
          viewBox="0 0 280 160"
          style={{ width: '100%', height: '100%', overflow: 'visible' }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="25%" stopColor="#f97316" />
              <stop offset="50%" stopColor="#eab308" />
              <stop offset="75%" stopColor="#22c55e" />
              <stop offset="100%" stopColor="#10b981" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          <path
            d={describeArc(180, 360)}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />

          <path
            d={describeArc(180, 360)}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={arcLength}
            strokeDashoffset={progressOffset}
            style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
          />

          {tickMarks.map((tick, i) => (
            <line
              key={i}
              x1={tick.x1}
              y1={tick.y1}
              x2={tick.x2}
              y2={tick.y2}
              stroke={tick.major ? '#9ca3af' : '#d1d5db'}
              strokeWidth={tick.major ? 2 : 1}
              strokeLinecap="round"
            />
          ))}

          <line
            x1={cx}
            y1={cy}
            x2={needleTip.x}
            y2={needleTip.y}
            stroke="#1f2937"
            strokeWidth={4}
            strokeLinecap="round"
            filter="url(#glow)"
          />

          <circle
            cx={cx}
            cy={cy}
            r={12}
            fill="#1f2937"
          />
          <circle
            cx={cx}
            cy={cy}
            r={6}
            fill={getScoreColor(animatedPct)}
          />

          <text
            x="30"
            y={cy + 20}
            fontSize="11"
            fill="#9ca3af"
            fontFamily="Inter, sans-serif"
            fontWeight="500"
          >
            0
          </text>
          <text
            x="245"
            y={cy + 20}
            fontSize="11"
            fill="#9ca3af"
            fontFamily="Inter, sans-serif"
            fontWeight="500"
          >
            {maxScore}
          </text>
        </svg>
        <ScoreDisplay>
          <ScoreValue>{displayScore}</ScoreValue>
          <ScoreMax>/ {maxScore}</ScoreMax>
        </ScoreDisplay>
      </SVGContainer>
      {showLabels && (
        <Box sx={{ textAlign: 'center' }}>
          <StatusBadge status={status}>
            <span>{status}</span>
          </StatusBadge>
        </Box>
      )}
    </GaugeWrapper>
  );
};

export default SemiCircularGauge;
